import { motion } from "framer-motion";
import { DollarSign, TrendingDown, Clock, AlertTriangle } from "lucide-react";

const ProblemSection = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const problems = [
    {
      icon: DollarSign,
      title: "High Commission Fees",
      description: "8-10% of every load goes to your dispatcher",
    },
    {
      icon: TrendingDown,
      title: "Lost Revenue",
      description: "On $30,000/month, that's $3,000 gone",
    },
    {
      icon: Clock,
      title: "Limited Availability",
      description: "Traditional dispatchers work 9-5",
    },
    {
      icon: AlertTriangle,
      title: "Poor Communication",
      description: "Phone tag and missed opportunities",
    },
  ];

  return (
    <section
      id="problem"
      className="py-20 md:py-32 bg-white"
      data-testid="problem-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="text-center mb-16">
          <span className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            The Problem
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary tracking-tight uppercase mb-6">
            Dispatchers Take Too Much.
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Most owner-operators pay 8–10% of every load to a dispatcher.
            On $30,000 monthly revenue, that's <span className="font-bold text-red-600">$3,000 gone every month</span>.
          </p>
        </motion.div>

        {/* Problem Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-slate-50 p-6 rounded-xl border border-slate-200"
              data-testid={`problem-card-${index}`}
            >
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center mb-4">
                <problem.icon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-primary mb-2">
                {problem.title}
              </h3>
              <p className="text-slate-600">{problem.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Solution Statement */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-primary noise-overlay rounded-2xl p-8 md:p-12 text-center relative overflow-hidden"
          data-testid="solution-statement"
        >
          <div className="relative z-10">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
              There's a <span className="text-accent">Better Way</span>
            </h3>
            <p className="text-lg md:text-xl text-slate-200 max-w-2xl mx-auto">
              MyLane replaces the traditional dispatcher with a smarter dispatch service
              that helps you <span className="font-bold text-accent">keep more of your money</span>.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;
